import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Servicio del módulo Inversiones exterior (DJ 1929)
 * Encapsula las llamadas al API /api/inversiones-exterior
 */
class InversionesExteriorService {
    /**
     * Configurar headers de autenticación
     */
    _getHeaders(token) {
        return {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }

    /**
     * Configurar headers para multipart/form-data
     */
    _getMultipartHeaders(token) {
        return {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        };
    }

    /**
     * Manejar errores de API
     */
    _handleError(error, defaultMessage) {
        if (error.response) {
            throw new Error(error.response.data.error || error.response.data.message || defaultMessage);
        } else if (error.request) {
            throw new Error('No se pudo conectar con el servidor');
        } else {
            throw new Error(error.message || defaultMessage);
        }
    }

    /**
     * Tasas al 31-12 del año de los movimientos (solo lectura)
     *
     * @param {number} anioComercial - Año de los movimientos
     * @param {string} token - Token de autenticación
     * @returns {Promise<Array>} Tasas del año, una por moneda
     */
    async getTiposCambio(anioComercial, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/inversiones-exterior/tipos-cambio`,
                {
                    params: { anio_comercial: anioComercial },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al obtener los tipos de cambio');
        }
    }

    /**
     * Subir el archivo de una fuente: el servidor lo lee y deja la carga «por revisar»
     *
     * @param {Object} datos - { idCompany, anioComercial, fuente, archivo }
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} { data: carga con filas y filas_con_aviso }
     */
    async subirCarga({ idCompany, anioComercial, fuente, archivo }, token) {
        try {
            const formData = new FormData();
            formData.append('archivo', archivo);
            formData.append('id_company', idCompany);
            formData.append('anio_comercial', anioComercial);
            formData.append('fuente', fuente);
            const response = await axios.post(
                `${API_BASE_URL}/api/inversiones-exterior/cargas`,
                formData,
                { headers: this._getMultipartHeaders(token) }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al subir el archivo');
        }
    }

    /**
     * Cargas de la empresa en un año, de una fuente
     *
     * @param {Object} filtros - { idCompany, anioComercial, fuente }
     * @param {string} token - Token de autenticación
     * @returns {Promise<Array>} Cargas sin las descartadas
     */
    async getCargas({ idCompany, anioComercial, fuente }, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/inversiones-exterior/cargas`,
                {
                    params: { id_company: idCompany, anio_comercial: anioComercial, fuente },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al obtener las cargas');
        }
    }

    /**
     * Una carga con sus filas, las columnas de la DJ 1929 y los totales
     *
     * @param {string} idCarga - ID de la carga
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Carga
     */
    async getCarga(idCarga, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/inversiones-exterior/cargas/${idCarga}`,
                { headers: this._getHeaders(token) }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al obtener la carga');
        }
    }

    /**
     * Editar una fila de una carga por revisar. Se guarda lo que viene, sin ajustes,
     * y el servidor vuelve a calcular los avisos.
     *
     * @param {string} idCarga - ID de la carga
     * @param {string} idFila - ID de la fila
     * @param {Object} datos - Columnas de la DJ 1929 (campo: valor)
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Fila guardada, con sus avisos
     */
    async editarFila(idCarga, idFila, datos, token) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/inversiones-exterior/cargas/${idCarga}/filas/${idFila}`,
                datos,
                { headers: this._getHeaders(token) }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al guardar la fila');
        }
    }

    /**
     * Agregar una fila escrita por el contador al final de una carga por revisar
     *
     * @param {string} idCarga - ID de la carga
     * @param {Object} datos - Columnas de la DJ 1929 (campo: valor)
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Fila agregada, con sus avisos
     */
    async agregarFila(idCarga, datos, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/inversiones-exterior/cargas/${idCarga}/filas`,
                datos,
                { headers: this._getHeaders(token) }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al agregar la fila');
        }
    }

    /**
     * Excluir una fila (no suma ni va a la DJ) o volver a incluirla
     *
     * @param {string} idCarga - ID de la carga
     * @param {string} idFila - ID de la fila
     * @param {boolean} incluida - true para incluirla, false para excluirla
     * @param {string} token - Token de autenticación
     */
    async cambiarInclusionFila(idCarga, idFila, incluida, token) {
        try {
            const response = await axios.patch(
                `${API_BASE_URL}/api/inversiones-exterior/cargas/${idCarga}/filas/${idFila}/incluida`,
                { incluida },
                { headers: this._getHeaders(token) }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al cambiar la fila');
        }
    }

    /**
     * Validar una carga por revisar: sus filas incluidas pasan a la DJ 1929
     *
     * @param {string} idCarga - ID de la carga
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} { id, estado, validado_en, filas, filas_con_aviso }
     */
    async validarCarga(idCarga, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/inversiones-exterior/cargas/${idCarga}/validar`,
                {},
                { headers: this._getHeaders(token) }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al validar la carga');
        }
    }

    /**
     * Reabrir una carga validada: vuelve a «por revisar» y deja de contar para la DJ
     *
     * @param {string} idCarga - ID de la carga
     * @param {string} token - Token de autenticación
     */
    async reabrirCarga(idCarga, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/inversiones-exterior/cargas/${idCarga}/reabrir`,
                {},
                { headers: this._getHeaders(token) }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al reabrir la carga');
        }
    }

    /**
     * Resumen de la DJ 1929 de un AT: cargas validadas y sin validar, sección C de
     * control y si se puede descargar
     *
     * @param {number} idCompany - Empresa
     * @param {number} anioTributario - AT (los movimientos son del año anterior)
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Resumen
     */
    async getResumenDJ1929(idCompany, anioTributario, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/inversiones-exterior/dj1929`,
                {
                    params: { id_company: idCompany, anio_tributario: anioTributario },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al obtener el resumen de la DJ 1929');
        }
    }

    /**
     * Descargar el CSV de la DJ 1929 para el importador del SII
     *
     * @param {number} idCompany - Empresa
     * @param {number} anioTributario - AT
     * @param {string} rutEmpresa - RUT de la empresa, para el nombre del archivo
     * @param {string} token - Token de autenticación
     */
    async descargarCSVDJ1929(idCompany, anioTributario, rutEmpresa, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/inversiones-exterior/dj1929/csv`,
                {
                    params: { id_company: idCompany, anio_tributario: anioTributario },
                    headers: this._getHeaders(token),
                    responseType: 'blob'
                }
            );
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = rutEmpresa ? `DJ1929_${rutEmpresa}_${anioTributario}.csv` : `DJ1929_${anioTributario}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            // Con responseType blob, el mensaje del servidor llega como Blob
            if (error.response?.data instanceof Blob) {
                try {
                    error.response.data = JSON.parse(await error.response.data.text());
                } catch {
                    error.response.data = {};
                }
            }
            this._handleError(error, 'Error al descargar el CSV de la DJ 1929');
        }
    }

    /**
     * Países de la tabla del módulo (sigla y nombre), para la columna País
     *
     * @param {string} token - Token de autenticación
     * @returns {Promise<Array>} Países vigentes
     */
    async getPaises(token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/inversiones-exterior/paises`,
                { headers: this._getHeaders(token) }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al obtener los países');
        }
    }

    /**
     * Descartar una carga por revisar: deja de contar para la DJ
     *
     * @param {string} idCarga - ID de la carga
     * @param {string} token - Token de autenticación
     */
    async descartarCarga(idCarga, token) {
        try {
            const response = await axios.patch(
                `${API_BASE_URL}/api/inversiones-exterior/cargas/${idCarga}/descartar`,
                {},
                { headers: this._getHeaders(token) }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al descartar la carga');
        }
    }
}

const inversionesExteriorService = new InversionesExteriorService();
export default inversionesExteriorService;
